import Pusher from "pusher-js";
import axios from "axios";

const PUSHER_KEY = process.env.REACT_APP_HITPAY_PUSHER_KEY || process.env.REACT_APP_PUSHER_KEY;
const PUSHER_CLUSTER =
  process.env.REACT_APP_HITPAY_PUSHER_CLUSTER || process.env.REACT_APP_PUSHER_CLUSTER || "ap1";
const AUTH_ENDPOINT =
  process.env.REACT_APP_HITPAY_BROADCAST_AUTH ||
  `${process.env.REACT_APP_API_BASE_URL || ""}hitpay/broadcast_auth`;

const readAuthToken = () => {
  try {
    return JSON.parse(localStorage.getItem("headers"))?.data?.token || null;
  } catch (err) {
    return null;
  }
};

const authParams = () => ({
  business_id: localStorage.getItem("business_id"),
  location_id: localStorage.getItem("location_id"),
});

// GET /hitpay/payment_status → data.payment_status: succeeded | pending | expired | refunded
export const fetchHitPayPaymentStatus = async (referenceNumber) => {
  const response = await axios.get("/hitpay/payment_status", {
    params: { reference_number: referenceNumber, ...authParams() },
  });
  return response?.data?.data?.payment_status;
};

// Watches one HitPay payment. Calls onResult("succeeded" | "failed") from whichever source
// answers first (Pusher event, or the payment_status check after subscribing / when the tab
// becomes visible again). The caller must treat onResult as possibly firing more than once.
// Returns a cleanup function.
export const watchHitPayPayment = (referenceNumber, onResult) => {
  let stopped = false;

  const check = async () => {
    try {
      const status = await fetchHitPayPaymentStatus(referenceNumber);
      if (stopped) return;
      if (status === "succeeded") onResult("succeeded");
      else if (status === "expired") onResult("failed");
    } catch (err) {
      console.warn("HitPay payment_status check failed:", err);
    }
  };

  const onVisible = () => {
    if (document.visibilityState === "visible") check();
  };
  document.addEventListener("visibilitychange", onVisible);

  let pusher = null;
  let channel = null;
  const token = readAuthToken();

  if (PUSHER_KEY && token) {
    pusher = new Pusher(PUSHER_KEY, {
      cluster: PUSHER_CLUSTER,
      forceTLS: true,
      channelAuthorization: {
        endpoint: AUTH_ENDPOINT,
        headers: { Authorization: `Bearer ${token}` },
        paramsProvider: authParams,
      },
    });
    const channelName = `private-hitpay-payment.${referenceNumber}`;
    channel = pusher.subscribe(channelName);
    channel.bind("pusher:subscription_error", (status) => {
      console.warn(`Pusher: could not subscribe to ${channelName}`, status);
    });
    channel.bind("pusher:subscription_succeeded", check);
    channel.bind("payment.status.updated", (data) => {
      if (stopped) return;
      if (data?.status === "succeeded") onResult("succeeded");
      else if (data?.status === "failed") onResult("failed");
    });
  } else {
    console.warn("HitPay realtime unavailable (missing Pusher key or login), using status checks only");
  }

  check();

  return () => {
    stopped = true;
    document.removeEventListener("visibilitychange", onVisible);
    if (channel) {
      channel.unbind_all();
      pusher.unsubscribe(channel.name);
    }
    if (pusher) pusher.disconnect();
  };
};
